import { useEffect, useRef } from 'react'

import { audioService } from '@shared/lib/audio'

import {
  CrowdEngine,
  DEFAULT_LITE_CROWD_THRESHOLD,
} from '../lib/CrowdEngine'
import { useCrowdAvatarsStore } from '../model/store'
import styles from './CrowdAvatarsLayer.module.css'

/** Live overlay walker cap. */
const LIVE_CROWD_SIZE = 80
/** Below this: all full KayKit. At this many: whole crowd flips to lite. */
const LITE_CROWD_THRESHOLD = DEFAULT_LITE_CROWD_THRESHOLD

interface Props {
  enabled?: boolean
}

/**
 * Persistent crowd layer at the bottom of the overlay.
 * < LITE_CROWD_THRESHOLD → all full KayKit;
 * ≥ LITE_CROWD_THRESHOLD → everyone lite (full only during spotlight/glory).
 */
export function CrowdAvatarsLayer({ enabled = true }: Props) {
  const hostRef = useRef<HTMLDivElement>(null)
  const engineRef = useRef<CrowdEngine | null>(null)
  const avatars = useCrowdAvatarsStore((s) => s.avatars)
  const actionQueue = useCrowdAvatarsStore((s) => s.actionQueue)
  const shiftAction = useCrowdAvatarsStore((s) => s.shiftAction)
  const sfxEnabled = useCrowdAvatarsStore((s) => s.sfxEnabled)

  useEffect(() => {
    const host = hostRef.current
    if (!host || !enabled) return

    const engine = new CrowdEngine(host, {
      maxAvatars: LIVE_CROWD_SIZE,
      liteCrowdThreshold: LITE_CROWD_THRESHOLD,
    })
    engineRef.current = engine
    engine.start()
    engine.setSfxEnabled(useCrowdAvatarsStore.getState().sfxEnabled)

    // Browser autoplay: unlock AudioContext on first gesture (OBS usually needs none)
    const unlockAudio = () => {
      void audioService.unlock()
    }
    window.addEventListener('pointerdown', unlockAudio, { once: true })
    void audioService.unlock()

    const onResize = () => engine.resize()
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('pointerdown', unlockAudio)
      window.removeEventListener('resize', onResize)
      engine.destroy()
      engineRef.current = null
    }
  }, [enabled])

  useEffect(() => {
    engineRef.current?.setAvatars(avatars)
  }, [avatars])

  useEffect(() => {
    engineRef.current?.setSfxEnabled(sfxEnabled)
  }, [sfxEnabled])

  useEffect(() => {
    const engine = engineRef.current
    if (!engine || actionQueue.length === 0) return
    for (;;) {
      const next = shiftAction()
      if (!next) break
      engine.enqueueAction(next)
    }
  }, [actionQueue, shiftAction])

  if (!enabled) return null

  return (
    <div
      ref={hostRef}
      className={styles.root}
      aria-hidden
      data-crowd-avatars='true'
    />
  )
}
